import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

interface UpdateMeetingParams {
  id: string;
  field: string;
  value: string;
}

export const useMeetingUpdate = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ id, field, value }: UpdateMeetingParams) => {
      console.log('Updating meeting:', { id, field, value });
      
      // Map field names to database columns
      const fieldMapping: { [key: string]: string } = {
        'meeting_date': 'meetings_booked_create_date',
        'created_date': 'meetings_booked_create_date',
        'client': 'client',
        'lead_email': 'lead_email',
        'campaign_name': 'campaign_name',
        'amount': 'amount',
        'close_date': 'close_date',
        'next_step': 'next_step',
        'stage_name': 'stage_name',
        'account_name': 'account_name',
        'lead_source': 'lead_source',
        'opportunity_name': 'opportunity_name'
      };

      const dbField = fieldMapping[field] || field;

      // Handle date formatting for date fields
      let processedValue = value;
      if (field === 'meeting_date' || field === 'created_date' || field === 'close_date') {
        // Convert DD/MM/YYYY format to ISO date
        if (value.includes('/')) {
          const [day, month, year] = value.split('/');
          processedValue = new Date(`${year}-${month}-${day}`).toISOString();
        }
      }

      // Phase 4b (Sabeeh, 2026-04-29): meetings_booked is now a thin
      // Clay landing table — the editable columns this hook used to
      // update (meeting_status, lead_email, etc.) no longer exist. Old
      // Pages routes that consume this hook can no longer mutate
      // meetings via the dashboard; that's expected for now since
      // meetings come from Clay/CRM, not human edits.
      console.warn(
        `useMeetingUpdate: legacy column ${dbField} no longer exists on meetings_booked. ` +
          'No-op until the dashboard offers meeting edits via the new schema.',
      )
      return null as any;

      // Original implementation (kept for reference — unreachable):
      // eslint-disable-next-line no-unreachable
      const { data, error } = await supabase
        .from('meetings_booked')
        .update({ [dbField]: processedValue })
        .eq('id', id)
        .select()
        .single();

      if (error) {
        console.error('Error updating meeting:', error);
        throw error;
      }

      return data;
    },
    onSuccess: (data, variables) => {
      toast({
        title: "Success",
        description: `Meeting ${variables.field} updated successfully`,
      });
      
      // Invalidate and refetch meeting data
      queryClient.invalidateQueries({ queryKey: ['meetings'] });
    },
    onError: (error, variables) => {
      console.error('Failed to update meeting:', error);
      toast({
        title: "Error",
        description: `Failed to update ${variables.field}: ${error.message}`,
        variant: "destructive",
      });
    },
  });
};